import secureApiService from './secureApiService'
import {
  Assignment,
  AssignmentDerivedStatus,
  AssignmentFilter,
  AssignmentFile,
  AssignmentListMeta,
  AssignmentListResponse,
  AssignmentStats,
  AssignmentSubmission,
  AssignmentSubmissionFile,
  GradeAssignmentPayload,
  SubmissionFilter,
  SubmissionListMeta,
  SubmissionListResponse,
  SubmitAssignmentPayload,
  CreateAssignmentPayload,
  UpdateAssignmentPayload,
} from '@/types/assignments'

type ApiSuccessEnvelope<T> = {
  success?: boolean
  message?: string
  data: T
  meta?: Record<string, unknown>
}

class AssignmentService {
  private readonly baseUrl = '/assignments'

  private unwrapResponse<T>(payload: ApiSuccessEnvelope<T> | T): T {
    if (payload && typeof payload === 'object' && 'data' in (payload as any)) {
      return (payload as ApiSuccessEnvelope<T>).data
    }
    return payload as T
  }

  private extractEnvelopeMeta(payload: ApiSuccessEnvelope<unknown> | unknown): Record<string, any> | undefined {
    if (payload && typeof payload === 'object' && 'meta' in (payload as any)) {
      return (payload as ApiSuccessEnvelope<unknown>).meta as Record<string, any>
    }
    return undefined
  }

  private computeDerivedStatus(
    dueDate: string,
    startDate?: string | null,
    isActive: boolean = true
  ): AssignmentDerivedStatus {
    if (!isActive) {
      return 'archived'
    }

    const now = new Date()
    const due = new Date(dueDate)
    const start = startDate ? new Date(startDate) : undefined

    if (start && start > now) {
      return 'scheduled'
    }

    if (due < now) {
      return 'closed'
    }

    return 'open'
  }

  private mapAssignmentFile(file: Record<string, any>): AssignmentFile {
    return {
      id: String(file.id),
      assignmentId: file.assignment_id ?? file.assignmentId ?? null,
      moduleId: file.module_id ?? file.moduleId ?? null,
      courseId: file.course_id ?? file.courseId ?? null,
      title: file.title ?? null,
      fileName: file.file_name ?? file.fileName ?? null,
      description: file.description ?? null,
      type: file.type ?? null,
      storageKey: file.storage_key ?? file.storageKey ?? null,
      url: file.url,
      fileSize: Number(file.file_size ?? file.fileSize ?? 0),
      mimeType: file.mime_type ?? file.mimeType ?? 'application/octet-stream',
      order: file.order ?? null,
      processingStatus: file.processing_status ?? file.processingStatus ?? null,
      isActive: file.is_active ?? file.isActive ?? true,
      createdAt: file.created_at ?? file.createdAt ?? new Date().toISOString(),
      updatedAt: file.updated_at ?? file.updatedAt ?? new Date().toISOString(),
      uploadedAt: file.uploaded_at ?? file.uploadedAt,
    }
  }

  private mapSubmissionFile(file: Record<string, any>): AssignmentSubmissionFile {
    return {
      id: String(file.id),
      submissionId: file.submission_id ?? file.submissionId,
      fileName: file.file_name ?? file.fileName ?? 'file',
      fileSize: Number(file.file_size ?? file.fileSize ?? 0),
      mimeType: file.mime_type ?? file.mimeType ?? 'application/octet-stream',
      url: file.url,
      uploadedAt: file.uploaded_at ?? file.uploadedAt ?? new Date().toISOString(),
    }
  }

  private mapAssignment(payload: Record<string, any>): Assignment {
    const attachments = Array.isArray(payload.attachments)
      ? payload.attachments.map((file: any) => this.mapAssignmentFile(file))
      : []

    const derivedStatus = this.computeDerivedStatus(
      payload.due_date ?? payload.dueDate,
      payload.start_date ?? payload.startDate,
      payload.is_active ?? payload.isActive ?? true
    )

    return {
      id: String(payload.id),
      courseId: String(payload.course_id ?? payload.courseId),
      title: payload.title ?? '',
      description: payload.description ?? null,
      instructions: payload.instructions ?? null,
      startDate: payload.start_date ?? payload.startDate ?? null,
      dueDate: payload.due_date ?? payload.dueDate ?? new Date().toISOString(),
      maxPoints: Number(payload.max_points ?? payload.maxPoints ?? 0),
      settings: payload.settings ?? {},
      creatorId: String(payload.creator_id ?? payload.creatorId ?? ''),
      timeLimitMinutes: payload.time_limit_minutes ?? payload.timeLimitMinutes ?? null,
      proctoringEnabled: payload.proctoring_enabled ?? payload.proctoringEnabled ?? false,
      isActive: payload.is_active ?? payload.isActive ?? true,
      createdAt: payload.created_at ?? payload.createdAt ?? new Date().toISOString(),
      updatedAt: payload.updated_at ?? payload.updatedAt ?? new Date().toISOString(),
      attachments,
      course: payload.course
        ? {
            id: String(payload.course.id ?? payload.courseId ?? payload.course_id),
            title: payload.course.title ?? payload.course.name ?? undefined,
            code: payload.course.code ?? undefined,
          }
        : undefined,
      derivedStatus,
    }
  }

  private mapSubmission(payload: Record<string, any>): AssignmentSubmission {
    const files = Array.isArray(payload.files)
      ? payload.files.map((file: any) => this.mapSubmissionFile(file))
      : []

    const gradePayload = payload.grade
    const grade = gradePayload
      ? {
          id: String(gradePayload.id),
          submissionId: String(
            gradePayload.submission_id ?? gradePayload.submissionId ?? payload.id
          ),
          points: Number(gradePayload.points ?? 0),
          maxPoints: gradePayload.max_points ?? gradePayload.maxPoints ?? undefined,
          feedback: gradePayload.feedback ?? null,
          gradedBy: gradePayload.graded_by ?? gradePayload.gradedBy ?? null,
          gradedAt: gradePayload.graded_at ?? gradePayload.gradedAt ?? null,
          createdAt: gradePayload.created_at ?? gradePayload.createdAt ?? null,
          updatedAt: gradePayload.updated_at ?? gradePayload.updatedAt ?? null,
        }
      : null

    return {
      id: String(payload.id),
      assignmentId: String(payload.assignment_id ?? payload.assignmentId),
      learnerId: String(payload.learner_id ?? payload.learnerId ?? ''),
      status: (payload.status ?? 'submitted') as AssignmentSubmission['status'],
      submittedAt: payload.submitted_at ?? payload.submittedAt ?? null,
      answers: payload.answers ?? null,
      late: payload.late ?? false,
      ipAddress: payload.ip_address ?? payload.ipAddress ?? null,
      userAgent: payload.user_agent ?? payload.userAgent ?? null,
      createdAt: payload.created_at ?? payload.createdAt ?? new Date().toISOString(),
      updatedAt: payload.updated_at ?? payload.updatedAt ?? new Date().toISOString(),
      files,
      grade,
    }
  }

  private extractSubmissionMeta(meta?: Record<string, any>): SubmissionListMeta | undefined {
    if (!meta) return undefined
    return {
      total:
        meta.total !== undefined
          ? Number(meta.total)
          : meta.count !== undefined
            ? Number(meta.count)
            : undefined,
      page:
        meta.page !== undefined
          ? Number(meta.page)
          : meta.currentPage !== undefined
            ? Number(meta.currentPage)
            : undefined,
      perPage:
        meta.perPage !== undefined
          ? Number(meta.perPage)
          : meta.per_page !== undefined
            ? Number(meta.per_page)
            : undefined,
      totalPages:
        meta.totalPages !== undefined
          ? Number(meta.totalPages)
          : meta.total_pages !== undefined
            ? Number(meta.total_pages)
            : undefined,
    }
  }

  async getAssignments(filters: AssignmentFilter = {}): Promise<AssignmentListResponse> {
    const params = new URLSearchParams()

    if (filters.courseId) {
      params.set('courseId', filters.courseId)
    }

    if (typeof filters.isActive === 'boolean') {
      params.set('isActive', String(filters.isActive))
    }

    if (filters.page) {
      params.set('page', String(filters.page))
    }

    if (filters.perPage) {
      params.set('perPage', String(filters.perPage))
    }

    const query = params.toString()
    const endpoint = query ? `${this.baseUrl}?${query}` : this.baseUrl

    try {
      const response = await secureApiService.get<ApiSuccessEnvelope<any>>(endpoint)
      const apiPayload = response.data
      const rawAssignments = this.unwrapResponse<any[]>(apiPayload)
      const mappedAssignments = Array.isArray(rawAssignments)
        ? rawAssignments.map((item: any) => this.mapAssignment(item))
        : []
      const mappedLength = mappedAssignments.length

      const rawMeta = this.extractEnvelopeMeta(apiPayload)
      const assignmentMeta = rawMeta
        ? {
            total:
              rawMeta.total !== undefined
                ? Number(rawMeta.total)
                : mappedLength,
            page: Number(rawMeta.page ?? filters.page ?? 1),
            perPage:
              rawMeta.perPage !== undefined
                ? Number(rawMeta.perPage)
                : Number(filters.perPage ?? (mappedLength || 25)),
            totalPages:
              rawMeta.totalPages !== undefined ? Number(rawMeta.totalPages) : undefined,
          }
        : undefined

      return {
        assignments: mappedAssignments,
        meta: assignmentMeta,
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error)
      return { assignments: [] }
    }
  }

  async getAssignment(assignmentId: string): Promise<Assignment | null> {
    try {
      const response = await secureApiService.get<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/${assignmentId}`
      )
      const payload = this.unwrapResponse<Record<string, any>>(response.data)
      return this.mapAssignment(payload)
    } catch (error) {
      console.error(`Failed to fetch assignment ${assignmentId}:`, error)
      return null
    }
  }

  async createAssignment(payload: CreateAssignmentPayload): Promise<Assignment | null> {
    const formData = new FormData()
    formData.append('courseId', payload.courseId)
    formData.append('title', payload.title)
    formData.append('description', payload.description)
    formData.append('dueDate', payload.dueDate)
    formData.append('maxPoints', String(payload.maxPoints))

    if (payload.instructions) {
      formData.append('instructions', payload.instructions)
    }

    if (payload.settings) {
      formData.append('settings', JSON.stringify(payload.settings))
    }

    payload.files?.forEach((file) => {
      formData.append('files', file)
    })

    try {
      const response = await secureApiService.postFormData<ApiSuccessEnvelope<any>>(
        this.baseUrl,
        formData
      )
      const created = this.unwrapResponse<Record<string, any>>(response.data)
      return this.mapAssignment(created)
    } catch (error) {
      console.error('Failed to create assignment:', error)
      return null
    }
  }

  async updateAssignment(payload: UpdateAssignmentPayload): Promise<Assignment | null> {
    const formData = new FormData()

    if (payload.courseId) {
      formData.append('courseId', payload.courseId)
    }

    if (payload.title) {
      formData.append('title', payload.title)
    }

    if (payload.description) {
      formData.append('description', payload.description)
    }

    if (payload.instructions !== undefined) {
      formData.append('instructions', payload.instructions ?? '')
    }

    if (payload.dueDate) {
      formData.append('dueDate', payload.dueDate)
    }

    if (payload.maxPoints !== undefined) {
      formData.append('maxPoints', String(payload.maxPoints))
    }

    if (payload.settings) {
      formData.append('settings', JSON.stringify(payload.settings))
    }

    payload.files?.forEach((file) => {
      formData.append('files', file)
    })

    if (payload.filesToRemove?.length) {
      formData.append('filesToRemove', JSON.stringify(payload.filesToRemove))
    }

    try {
      const response = await secureApiService.putFormData<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/${payload.id}`,
        formData
      )
      const updated = this.unwrapResponse<Record<string, any>>(response.data)
      return this.mapAssignment(updated)
    } catch (error) {
      console.error(`Failed to update assignment ${payload.id}:`, error)
      return null
    }
  }

  async deleteAssignment(assignmentId: string): Promise<boolean> {
    try {
      await secureApiService.delete<ApiSuccessEnvelope<unknown>>(
        `${this.baseUrl}/${assignmentId}`
      )
      return true
    } catch (error) {
      console.error(`Failed to delete assignment ${assignmentId}:`, error)
      return false
    }
  }

  async submitAssignment(payload: SubmitAssignmentPayload): Promise<AssignmentSubmission | null> {
    const formData = new FormData()
    formData.append('submit', payload.submit === false ? 'false' : 'true')

    if (payload.answers) {
      formData.append('answers', JSON.stringify(payload.answers))
    }

    payload.files?.forEach((file) => {
      formData.append('files', file)
    })

    try {
      const response = await secureApiService.postFormData<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/${payload.assignmentId}/submit`,
        formData
      )
      const submission = this.unwrapResponse<Record<string, any>>(response.data)
      return this.mapSubmission(submission)
    } catch (error) {
      console.error(`Failed to submit assignment ${payload.assignmentId}:`, error)
      return null
    }
  }

  async getSubmissions(
    assignmentId: string,
    filter: SubmissionFilter = {}
  ): Promise<SubmissionListResponse> {
    const params = new URLSearchParams()

    if (filter.page) {
      params.set('page', String(filter.page))
    }

    if (filter.perPage) {
      params.set('perPage', String(filter.perPage))
    }

    if (filter.status) {
      params.set('status', filter.status)
    }

    const query = params.toString()
    const endpoint = query
      ? `${this.baseUrl}/${assignmentId}/submissions?${query}`
      : `${this.baseUrl}/${assignmentId}/submissions`

    try {
      const response = await secureApiService.get<ApiSuccessEnvelope<any>>(endpoint)
      const apiPayload = response.data
      const rawSubmissions = this.unwrapResponse<any[]>(apiPayload)
      const mapped = Array.isArray(rawSubmissions)
        ? rawSubmissions.map((item: any) => this.mapSubmission(item))
        : []

      return {
        submissions: mapped,
        meta: this.extractSubmissionMeta(this.extractEnvelopeMeta(apiPayload)),
      }
    } catch (error) {
      console.error(`Failed to fetch submissions for assignment ${assignmentId}:`, error)
      return { submissions: [] }
    }
  }

  async getSubmission(
    assignmentId: string,
    submissionId: string
  ): Promise<AssignmentSubmission | null> {
    try {
      const response = await secureApiService.get<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/${assignmentId}/submissions/${submissionId}`
      )
      const payload = this.unwrapResponse<Record<string, any>>(response.data)
      return this.mapSubmission(payload)
    } catch (error) {
      console.error(
        `Failed to fetch submission ${submissionId} for assignment ${assignmentId}:`,
        error
      )
      return null
    }
  }

  async reopenSubmission(submissionId: string): Promise<AssignmentSubmission | null> {
    try {
      const response = await secureApiService.post<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/submissions/${submissionId}/reopen`
      )
      const payload = this.unwrapResponse<Record<string, any>>(response.data)
      return this.mapSubmission(payload)
    } catch (error) {
      console.error(`Failed to reopen submission ${submissionId}:`, error)
      return null
    }
  }

  async gradeSubmission(payload: GradeAssignmentPayload): Promise<boolean> {
    try {
      await secureApiService.post<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/submissions/${payload.submissionId}/grade`,
        {
          points: payload.points,
          feedback: payload.feedback,
        }
      )
      return true
    } catch (error) {
      console.error(`Failed to grade submission ${payload.submissionId}:`, error)
      return false
    }
  }

  async getAssignmentStats(): Promise<AssignmentStats | null> {
    try {
      const response = await secureApiService.get<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/stats`
      )
      const stats = this.unwrapResponse<Record<string, any>>(response.data)
      return {
        totalAssignments: stats.totalAssignments ?? stats.total ?? 0,
        activeAssignments: stats.activeAssignments ?? stats.active ?? 0,
        dueSoonAssignments: stats.dueSoonAssignments ?? stats.dueSoon ?? 0,
        submittedSubmissions: stats.submittedSubmissions ?? stats.submitted ?? 0,
        gradedSubmissions: stats.gradedSubmissions ?? stats.graded ?? 0,
      }
    } catch (error) {
      console.error('Failed to fetch assignment stats:', error)
      return null
    }
  }

  async downloadFile(fileUrl: string, fileName: string): Promise<void> {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(fileUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })

      if (!response.ok) {
        throw new Error(`Unable to download file: ${response.statusText}`)
      }

      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
      console.error('Failed to download file:', error)
      throw error
    }
  }

  async getMySubmissions(): Promise<AssignmentSubmission[]> {
    try {
      const response = await secureApiService.get<ApiSuccessEnvelope<any>>(
        `${this.baseUrl}/submissions/me`
      )
      const submissions = this.unwrapResponse<any[]>(response.data)
      return Array.isArray(submissions)
        ? submissions.map((submission: any) => this.mapSubmission(submission))
        : []
    } catch (error) {
      console.error('Failed to fetch learner submissions:', error)
      return []
    }
  }

  async getMySubmissionForAssignment(assignmentId: string): Promise<AssignmentSubmission | null> {
    try {
      const submissions = await this.getMySubmissions()
      return submissions.find((submission) => submission.assignmentId === assignmentId) ?? null
    } catch (error) {
      console.error(`Failed to fetch learner submission for assignment ${assignmentId}:`, error)
      return null
    }
  }
}

export const assignmentService = new AssignmentService()
export default assignmentService
